/**
 * Waypoint Validator Utility
 *
 * Validation functions for waypoints and missions
 * Ensures safety and compliance with limits
 */

import type { Waypoint, WaypointValidation } from '../types/waypoint.types';
import type { Mission } from '../types/mission.types';
import { VALIDATION_LIMITS } from '@/constants/mission-defaults';
import { calculateDistance } from './mission-calculator';
import { MavCommand } from '@/constants/mavlink-commands';

/**
 * Validate coordinate values
 *
 * @param latitude - Latitude in degrees
 * @param longitude - Longitude in degrees
 * @returns Validation result
 */
export function validateCoordinates(
  latitude: number,
  longitude: number
): WaypointValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate latitude
  if (latitude < VALIDATION_LIMITS.minLatitude || latitude > VALIDATION_LIMITS.maxLatitude) {
    errors.push(`Latitude must be between ${VALIDATION_LIMITS.minLatitude} and ${VALIDATION_LIMITS.maxLatitude} degrees`);
  }

  // Validate longitude
  if (longitude < VALIDATION_LIMITS.minLongitude || longitude > VALIDATION_LIMITS.maxLongitude) {
    errors.push(`Longitude must be between ${VALIDATION_LIMITS.minLongitude} and ${VALIDATION_LIMITS.maxLongitude} degrees`);
  }

  // Check if coordinates are (0, 0) - likely an error
  if (latitude === 0 && longitude === 0) {
    warnings.push('Coordinates are at (0, 0) - Null Island. This is likely unintentional.');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate altitude
 *
 * @param altitude - Altitude in meters
 * @param terrainAltitude - Optional terrain altitude at this location
 * @returns Validation result
 */
export function validateAltitude(
  altitude: number,
  terrainAltitude?: number
): WaypointValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if altitude is negative
  if (altitude < 0) {
    errors.push('Altitude cannot be negative');
  }

  // Check minimum safe altitude
  if (altitude < VALIDATION_LIMITS.minSafeAltitude) {
    errors.push(`Altitude must be at least ${VALIDATION_LIMITS.minSafeAltitude}m for safety`);
  }

  // Check maximum altitude
  if (altitude > VALIDATION_LIMITS.maxSafeAltitude) {
    warnings.push(`Altitude ${altitude}m exceeds recommended maximum of ${VALIDATION_LIMITS.maxSafeAltitude}m`);
  }

  // Check terrain clearance if terrain data available
  if (terrainAltitude !== undefined) {
    const clearance = altitude - terrainAltitude;
    if (clearance < VALIDATION_LIMITS.minSafeAltitude) {
      errors.push(`Insufficient terrain clearance: ${clearance.toFixed(1)}m. Minimum ${VALIDATION_LIMITS.minSafeAltitude}m required.`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate distance between waypoints
 *
 * @param wp1 - First waypoint
 * @param wp2 - Second waypoint
 * @returns Validation result
 */
export function validateDistance(
  wp1: Waypoint,
  wp2: Waypoint
): WaypointValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  const distance = calculateDistance(
    wp1.latitude,
    wp1.longitude,
    wp2.latitude,
    wp2.longitude
  );

  // Check if waypoints are too far apart
  if (distance > VALIDATION_LIMITS.maxWaypointDistance) {
    warnings.push(
      `Distance ${(distance / 1000).toFixed(2)}km exceeds ${(VALIDATION_LIMITS.maxWaypointDistance / 1000).toFixed(1)}km. ` +
      'Consider adding intermediate waypoints for signal reliability.'
    );
  }

  // Check if waypoints are too close
  if (distance < VALIDATION_LIMITS.minWaypointDistance) {
    warnings.push(
      `Waypoints are very close (${distance.toFixed(1)}m). ` +
      'GPS accuracy may cause issues.'
    );
  }

  // Check if waypoints are at the exact same location
  if (distance === 0) {
    errors.push('Waypoints cannot be at the exact same location');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate command parameters
 *
 * @param command - MAVLink command type
 * @param params - Array of param1-4 values
 * @returns Validation result
 */
export function validateCommandParams(
  command: MavCommand,
  params: [number, number, number, number]
): WaypointValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate specific command parameters
  switch (command) {
    case MavCommand.NAV_WAYPOINT:
      // param1: Hold time (seconds) - must be >= 0
      if (params[0] < 0) {
        errors.push('Hold time cannot be negative');
      }
      // param2: Acceptance radius (meters) - must be > 0
      if (params[1] <= 0) {
        errors.push('Acceptance radius must be greater than 0');
      }
      if (params[1] < 1) {
        warnings.push('Acceptance radius < 1m may be too precise for GPS accuracy');
      }
      // param4: Yaw angle (degrees) - must be -180 to 180
      if (params[3] < -180 || params[3] > 180) {
        errors.push('Yaw angle must be between -180 and 180 degrees');
      }
      break;

    case MavCommand.NAV_TAKEOFF:
      // param4: Yaw angle
      if (params[3] < -180 || params[3] > 180) {
        errors.push('Yaw angle must be between -180 and 180 degrees');
      }
      break;

    case MavCommand.NAV_LOITER_TIME:
      // param1: Loiter time (seconds) - must be > 0
      if (params[0] <= 0) {
        errors.push('Loiter time must be greater than 0');
      }
      if (params[0] > 3600) {
        warnings.push('Loiter time > 1 hour. Ensure sufficient battery.');
      }
      break;

    case MavCommand.DO_CHANGE_SPEED:
      // param1: Speed type (0 or 1)
      if (params[0] !== 0 && params[0] !== 1) {
        errors.push('Speed type must be 0 (airspeed) or 1 (ground speed)');
      }
      // param2: Speed (m/s) - must be > 0
      if (params[1] <= 0) {
        errors.push('Speed must be greater than 0');
      }
      if (params[1] > 20) {
        warnings.push('Speed > 20 m/s may exceed vehicle capabilities');
      }
      break;
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate a single waypoint
 *
 * @param waypoint - Waypoint to validate
 * @param prevWaypoint - Previous waypoint (if any) for distance checking
 * @returns Validation result
 */
export function validateWaypoint(
  waypoint: Waypoint,
  prevWaypoint?: Waypoint
): WaypointValidation {
  const allErrors: string[] = [];
  const allWarnings: string[] = [];

  // Validate coordinates
  const coordValidation = validateCoordinates(waypoint.latitude, waypoint.longitude);
  allErrors.push(...coordValidation.errors);
  allWarnings.push(...coordValidation.warnings);

  // Validate altitude
  const altValidation = validateAltitude(waypoint.altitude);
  allErrors.push(...altValidation.errors);
  allWarnings.push(...altValidation.warnings);

  // Validate distance to previous waypoint
  if (prevWaypoint) {
    const distValidation = validateDistance(prevWaypoint, waypoint);
    allErrors.push(...distValidation.errors);
    allWarnings.push(...distValidation.warnings);
  }

  // Validate command parameters
  const paramsValidation = validateCommandParams(waypoint.command, [
    waypoint.param1,
    waypoint.param2,
    waypoint.param3,
    waypoint.param4,
  ]);
  allErrors.push(...paramsValidation.errors);
  allWarnings.push(...paramsValidation.warnings);

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
  };
}

/**
 * Validate entire mission
 *
 * @param mission - Mission to validate
 * @returns Validation result
 */
export function validateMission(mission: Mission): WaypointValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if mission has waypoints
  if (mission.waypoints.length === 0) {
    errors.push('Mission must have at least one waypoint');
    return { isValid: false, errors, warnings };
  }

  // Check minimum waypoints
  if (mission.waypoints.length < VALIDATION_LIMITS.minWaypoints) {
    warnings.push(
      `Mission has only ${mission.waypoints.length} waypoint(s). ` +
      `Recommended minimum is ${VALIDATION_LIMITS.minWaypoints} (takeoff + land).`
    );
  }

  // Check maximum waypoints
  if (mission.waypoints.length > VALIDATION_LIMITS.maxWaypoints) {
    errors.push(
      `Mission has ${mission.waypoints.length} waypoints, ` +
      `exceeding maximum of ${VALIDATION_LIMITS.maxWaypoints}`
    );
  }

  // Validate first command should be TAKEOFF or WAYPOINT
  const firstWp = mission.waypoints[0];
  if (
    firstWp.command !== MavCommand.NAV_TAKEOFF &&
    firstWp.command !== MavCommand.NAV_WAYPOINT
  ) {
    warnings.push('First waypoint should typically be TAKEOFF or WAYPOINT');
  }

  // Validate last command should be LAND or RTL
  const lastWp = mission.waypoints[mission.waypoints.length - 1];
  if (
    lastWp.command !== MavCommand.NAV_LAND &&
    lastWp.command !== MavCommand.NAV_RETURN_TO_LAUNCH
  ) {
    warnings.push('Last waypoint should typically be LAND or RETURN_TO_LAUNCH');
  }

  // Validate each waypoint
  for (let i = 0; i < mission.waypoints.length; i++) {
    const waypoint = mission.waypoints[i];
    const prevWaypoint = i > 0 ? mission.waypoints[i - 1] : undefined;

    const wpValidation = validateWaypoint(waypoint, prevWaypoint);

    // Add waypoint-specific errors/warnings with context
    wpValidation.errors.forEach((error) => {
      errors.push(`Waypoint ${i + 1}: ${error}`);
    });
    wpValidation.warnings.forEach((warning) => {
      warnings.push(`Waypoint ${i + 1}: ${warning}`);
    });
  }

  // Validate sequence numbers
  const hasSequenceGaps = mission.waypoints.some(
    (wp, index) => wp.sequence !== index
  );
  if (hasSequenceGaps) {
    errors.push('Waypoint sequence numbers have gaps. Mission may not execute correctly.');
  }

  // Check battery estimate
  if (mission.estimatedBattery > 100 - VALIDATION_LIMITS.minBatteryReserve) {
    warnings.push(
      `Mission requires ${mission.estimatedBattery.toFixed(1)}% battery, ` +
      `leaving < ${VALIDATION_LIMITS.minBatteryReserve}% reserve. Consider shortening mission.`
    );
  }

  if (mission.estimatedBattery > 100) {
    errors.push(
      `Mission requires ${mission.estimatedBattery.toFixed(1)}% battery, ` +
      'which exceeds available capacity. Mission cannot be completed on single battery.'
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Quick validation check (only critical errors)
 *
 * @param mission - Mission to validate
 * @returns True if mission is valid (no errors), false otherwise
 */
export function isMissionValid(mission: Mission): boolean {
  const validation = validateMission(mission);
  return validation.isValid;
}

/**
 * Get validation summary text
 *
 * @param validation - Validation result
 * @returns Human-readable summary
 */
export function getValidationSummary(validation: WaypointValidation): string {
  if (validation.isValid && validation.warnings.length === 0) {
    return 'All checks passed';
  }

  if (!validation.isValid) {
    return `${validation.errors.length} error(s) found`;
  }

  return `${validation.warnings.length} warning(s)`;
}
